import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import AgntLogo from 'agnt/components/agnt-logo';
import { createHeaderConfig } from 'tenants/definitions/agnt-inc/header-config';
import styles from './style.module.scss';

const { menuItems } = createHeaderConfig();

function isNavLinkActive(link: string, pathname: string): boolean {
  if (link === '/') return pathname === '/';
  return pathname === link || pathname.startsWith(`${link}/`);
}

function BotIcon() {
  return (
    <svg
      className={styles.btnAskIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="4" y="7" width="16" height="12" rx="3" />
      <circle cx="9" cy="13" r="1.4" fill="currentColor" />
      <circle cx="15" cy="13" r="1.4" fill="currentColor" />
      <path d="M12 4v3" />
      <circle cx="12" cy="3" r="1" fill="currentColor" />
    </svg>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      {open ? (
        <>
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );
}

type AgntIncHeaderProps = {
  forceBackground?: boolean;
  highlightAlerts?: boolean;
};

export default function AgntIncHeader({ forceBackground = false, highlightAlerts = false }: AgntIncHeaderProps) {
  const { pathname, events } = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu on navigation and on Escape.
  useEffect(() => {
    const close = () => setMenuOpen(false);
    events?.on('routeChangeStart', close);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      events?.off('routeChangeStart', close);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [events]);

  const showBackground = forceBackground || menuOpen;

  return (
    <header className={`${styles.header}${showBackground ? ` ${styles.forceBackground}` : ''}`}>
      <div className={styles.inner}>
        <Link href="/" className={styles.logo} aria-label="AGNT home">
          <AgntLogo className={styles.logoSvg} />
        </Link>

        <nav className={styles.nav} aria-label="Main navigation">
          <ul className={styles.navLinks}>
            {menuItems.map(item => (
              <li key={item.id}>
                <Link
                  href={item.link}
                  className={`${styles.navLink} ${isNavLinkActive(item.link, pathname) ? styles.active : ''}`}
                >
                  {item.label.defaultValue}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.actions}>
          <a href="#alerts" className={`${styles.btnAlerts}${highlightAlerts ? ` ${styles.btnAlertsHighlight}` : ''}`}>
            Investor Alerts
          </a>
          <button
            type="button"
            className={styles.btnAsk}
            onClick={() => (window as any).AskAGNT?.toggle()}
          >
            <BotIcon />
            <span>Ask AGNT</span>
          </button>
          <button
            type="button"
            className={styles.hamburger}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="agnt-mobile-menu"
            onClick={() => setMenuOpen(open => !open)}
          >
            <MenuIcon open={menuOpen} />
          </button>
        </div>
      </div>

      <nav
        id="agnt-mobile-menu"
        className={`${styles.mobileMenu}${menuOpen ? ` ${styles.mobileMenuOpen}` : ''}`}
        aria-label="Mobile navigation"
      >
        <ul className={styles.mobileNavLinks}>
          {menuItems.map(item => (
            <li key={item.id}>
              <Link
                href={item.link}
                className={`${styles.mobileNavLink} ${isNavLinkActive(item.link, pathname) ? styles.active : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {item.label.defaultValue}
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={styles.mobileAsk}
          onClick={() => {
            setMenuOpen(false);
            (window as any).AskAGNT?.toggle();
          }}
        >
          <BotIcon />
          <span>Ask AGNT</span>
        </button>
      </nav>
    </header>
  );
}
